import React, { useState, useEffect } from 'react';
import { differenceInDays } from 'date-fns';
import { Target, ListTodo, TrendingUp, Check, X } from 'lucide-react';
import { 
  formatPersianDateLong, 
  persianNumber, 
  getPersianDateDetails,
  toGregorian,
  PERSIAN_MONTHS
} from '../services/utils';

interface DashboardProps {
  deadline: string | null;
  setDeadline: (date: string) => void;
  taskCount: number;
}

const Dashboard: React.FC<DashboardProps> = ({ deadline, setDeadline, taskCount }) => {
  const [isEditingDeadline, setIsEditingDeadline] = useState(false);
  
  // Persian Date Picker State
  const [pDay, setPDay] = useState(1);
  const [pMonth, setPMonth] = useState(1);
  const [pYear, setPYear] = useState(1403);

  // Initialize state when editing starts
  useEffect(() => {
    if (isEditingDeadline) {
      const date = deadline ? new Date(deadline) : new Date();
      const { jy, jm, jd } = getPersianDateDetails(date);
      setPYear(jy);
      setPMonth(jm);
      setPDay(jd);
    }
  }, [isEditingDeadline, deadline]);

  const saveDeadline = () => {
    // Convert Persian to Gregorian
    const { gy, gm, gd } = toGregorian(pYear, pMonth, pDay);
    const date = new Date(gy, gm - 1, gd);
    
    // Format as YYYY-MM-DD
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    
    setDeadline(`${y}-${m}-${d}`);
    setIsEditingDeadline(false);
  };

  const calculateDaysLeft = () => {
    if (!deadline) return 0;
    const diff = differenceInDays(new Date(deadline), new Date());
    return diff > 0 ? diff : 0;
  };

  const daysLeft = calculateDaysLeft();

  // Generate ranges
  const currentJYear = getPersianDateDetails(new Date()).jy;
  const years = Array.from({length: 6}, (_, i) => currentJYear + i); // Current year + 5
  const days = Array.from({length: 31}, (_, i) => i + 1);

  return (
    <div className="min-h-full flex flex-col gap-6 pb-4">
      {/* Hero Section: Countdown */}
      <div className="w-full bg-slate-900 dark:bg-slate-950 rounded-3xl p-6 md:p-12 text-white relative overflow-hidden shadow-2xl flex-shrink-0 border border-slate-800 transition-all">
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8">
          <div className="w-full md:w-auto">
            <h2 className="text-slate-400 text-sm md:text-lg uppercase tracking-wider font-medium mb-1">موعد مقرر (ددلاین)</h2>
            
            {isEditingDeadline ? (
              <div className="flex items-center gap-2 mt-2 bg-slate-800/80 p-2 rounded-lg backdrop-blur-sm border border-slate-700 inline-flex flex-wrap z-20 relative max-w-full">
                <select 
                  value={pDay}
                  onChange={(e) => setPDay(Number(e.target.value))}
                  className="bg-slate-900 text-white text-sm rounded p-1.5 border border-slate-600 outline-none focus:border-primary-500"
                >
                  {days.map(d => <option key={d} value={d}>{persianNumber(d)}</option>)}
                </select>
                
                <select 
                  value={pMonth}
                  onChange={(e) => setPMonth(Number(e.target.value))}
                  className="bg-slate-900 text-white text-sm rounded p-1.5 border border-slate-600 outline-none focus:border-primary-500"
                >
                  {PERSIAN_MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                </select>

                <select 
                  value={pYear}
                  onChange={(e) => setPYear(Number(e.target.value))}
                  className="bg-slate-900 text-white text-sm rounded p-1.5 border border-slate-600 outline-none focus:border-primary-500"
                >
                  {years.map(y => <option key={y} value={y}>{persianNumber(y)}</option>)}
                </select>

                <div className="flex items-center gap-1 mr-2 border-r border-slate-600 pr-2">
                  <button onClick={saveDeadline} className="bg-green-600 hover:bg-green-500 text-white p-1.5 rounded-md transition-colors">
                    <Check size={16} />
                  </button>
                  <button onClick={() => setIsEditingDeadline(false)} className="bg-slate-600 hover:bg-slate-500 text-white p-1.5 rounded-md transition-colors">
                    <X size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="group cursor-pointer flex items-center gap-3 flex-wrap"
                onClick={() => setIsEditingDeadline(true)}
              >
                <h1 className="text-2xl md:text-3xl font-bold">
                  {deadline ? formatPersianDateLong(new Date(deadline)) : 'تعیین تاریخ'}
                </h1>
                <span className="opacity-100 md:opacity-0 group-hover:opacity-100 text-xs bg-slate-800 px-2 py-1 rounded transition-opacity border border-slate-700">ویرایش</span>
              </div>
            )}
            
            <p className="mt-4 text-slate-400 text-sm md:text-base max-w-md leading-relaxed hidden sm:block">
              نگاهتان به هدف باشد. «گلنس» به شما کمک می‌کند تا دقیقاً ببینید چقدر زمان تا تسلط کامل بر دروس باقی مانده است.
            </p>
          </div>

          <div className="text-center md:text-left w-full md:w-auto bg-slate-800/50 md:bg-transparent rounded-2xl p-4 md:p-0">
            <div className="text-7xl sm:text-8xl lg:text-[8rem] leading-none font-bold tracking-tighter bg-clip-text text-transparent bg-gradient-to-b from-white to-slate-500">
              {persianNumber(daysLeft)}
            </div>
            <div className="text-sm md:text-xl font-medium text-slate-400 uppercase tracking-widest ml-2">روز باقی‌مانده</div>
          </div>
        </div>

        {/* Decorative Grid */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-soft-light pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-primary-500 rounded-full mix-blend-overlay filter blur-[60px] md:blur-[100px] opacity-20 -translate-y-1/2 -translate-x-1/3"></div>
      </div>

      {/* Lower Section: Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 flex-1">
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center mb-4">
            <ListTodo size={24} />
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white mb-1">{persianNumber(taskCount)}</h3>
          <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">وظایف فعال</p>
        </div>

        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full flex items-center justify-center mb-4">
            <Target size={24} />
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white mb-1">
            {persianNumber(Math.round(taskCount > 0 ? (1 / Math.max(daysLeft, 1)) * taskCount : 0))}
          </h3>
          <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">هدف وظایف / روز</p>
        </div>

        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mb-4">
            <TrendingUp size={24} />
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white mb-1">عالی</h3>
          <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">وضعیت فعلی</p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;